import type { Road, World } from './types.js';
import type { Heightfield } from './heightfield.js';
import { KERB_MIN_ROAD_WIDTH, KERB_STONE_WIDTH, ROAD_SPECS } from './osm/classify.js';
import { pavementReach } from './osm/pavements.js';
import { WaterIndex, waterLevels } from './water.js';
import { ribbon } from './geom.js';

/**
 * Bridges and tunnels: where their surface is, and who is standing on one.
 *
 * This lives in `shared/` rather than in the renderer because a deck is not a drawing
 * decision. It used to be: `worldMesh` spanned a bridge between its abutments so the
 * ribbon stopped following the riverbed, and nothing else in the game was told. The car's
 * height is sampled straight off the terrain, so on Most Grunwaldzki it drove three metres
 * under the deck it was supposedly crossing, and on every Seine bridge it drove under the
 * river as well. One description of the surface, used by the mesh and by the physics, is
 * the only way those two can agree.
 *
 * A bridge is described as a *structure* — every bridge way joined to another through a
 * shared node — rather than way by way, and it is lifted over whatever it crosses, not only
 * over water. Both of those are DECISIONS.md §72; the short version is that a viaduct in a
 * flat town is ten OSM ways with junctions on top, the DEM knows nothing about it, and
 * profiled one way at a time it lay on the ground with the street it spans driving through
 * it.
 */

/** The height of a deck at a point, in place of the ground there. */
export type DeckProfile = (x: number, z: number) => number;

/** The height of a deck at a point, and the gradient a car on it feels. */
export interface DeckSample {
  y: number;
  /** Uphill-positive, same convention as `Heightfield.slopeAt`. */
  gradeX: number;
  gradeZ: number;
}

/**
 * How far above the water a bridge deck has to be carried.
 *
 * Enough to read as a bridge from the chase camera without the approach turning into a
 * ramp: 2.5 m is roughly the headroom under the low arches of the Marais crossings, and
 * on a 90 m span the arch it implies is a 3% grade at the abutment.
 */
export const BRIDGE_CLEARANCE = 2.5;

/**
 * Over a carriageway: the 4.7 m a Polish or French overpass has to leave a lorry, plus the
 * deck it is drawn with. Over a footway, a person and a bit.
 */
export const ROAD_HEADROOM = 5;
const PATH_HEADROOM = 2.8;

/**
 * What a bridge is assumed to cross when nothing it crosses is in the payload.
 *
 * The streams OSM maps as lines are not fetched, and a region baked before railways were
 * read has no railway either, so a viaduct can arrive with nothing under it at all. Every
 * bridge spans *something*; this is the clearance of an electrified line, and the ramps
 * below are what stop it putting a footbridge over a ditch six metres in the air — a span
 * that short never gets near it.
 */
export const UNSEEN_HEADROOM = 6;

/**
 * Over a railway: a train and the deck above it. Now that the railway is in the payload a
 * viaduct clears the track where the track actually is, rather than wherever the middle of its
 * span happens to be — and a train passing under the ramp end of a long viaduct no longer goes
 * through it.
 */
export const RAIL_HEADROOM = 6;

/**
 * Over an electrified line, its overhead wire as well. Six metres was meant to cover that too
 * and did not: with the deck drawn 1.1 m thick, the soffit came 4.5 m over the rail head. The
 * wire is drawn at 5.5 m, so on the Leszno viaduct it ran through the deck, and the catenary
 * stood above the road. This leaves the half metre of ballast and rail, a contact wire let
 * down to 5 m as it is under a real bridge, the messenger squeezed in over it, some air, and
 * the deck. `railways.ts` dips the wire under whatever deck is actually there, in case a ramp
 * had too little room to climb this high.
 */
export const WIRED_RAIL_HEADROOM = 7.2;

/** How deep a bridge deck is drawn, surface to soffit. */
export const DECK_DEPTH = 1.1;

/**
 * The width a railway is treated as for decks: a single track's ballast shoulder to shoulder.
 * Each OSM way is one track, so a double-track bridge is two of these side by side, which is
 * what it is.
 */
export const RAIL_DECK_WIDTH = 4;

/**
 * The railway, dressed as roads, for the bridge builder.
 *
 * A railway bridge is a bridge like any other — a structure of ways joined through their
 * nodes, lifted over whatever it crosses and ramped back down to the ground at its ends — and
 * describing it twice would let a train and the deck under it disagree. So the track goes
 * through the same code as a road, as a `path` (nothing drives on it), and every track at
 * grade is something a road bridge has to clear. A tram in the street is left out: its
 * surface is the street's, bridge or not.
 */
export function railDeckRoads(world: World): Road[] {
  const out: Road[] = [];
  for (const line of world.rail?.lines ?? []) {
    if (line.embedded || line.deck === 'tunnel') continue;
    const road: Road = { id: line.id, cls: 'path', width: RAIL_DECK_WIDTH, oneway: false, y: 0, pts: line.pts, nodes: line.nodes };
    if (line.deck) road.deck = line.deck;
    if (line.layer) road.layer = line.layer;
    out.push(road);
  }
  return out;
}

/**
 * Ceiling on the lift, in metres.
 *
 * A DEM that has misread a valley floor could otherwise ask for a bridge into orbit. Eight
 * metres clears a road on an embankment under a road on a viaduct, which is the tallest
 * thing a city centre stacks.
 */
const MAX_ARCH = 8;

/**
 * Average gradient of a ramp up to the lift, and the steepest one is squeezed to before the
 * lift is allowed to fall short instead.
 *
 * The ramp is a smoothstep, whose steepest point is one and a half times its average, so 6%
 * peaks at 9% — a steep city ramp, and about what a real viaduct approach is.
 */
const RAMP_GRADE = 0.06;
const STEEP_GRADE = 0.12;

/**
 * How far a ramp may run back down the street leading onto a bridge.
 *
 * OSM tags the span as the bridge and nothing else: the embankment that climbs to it is an
 * ordinary way, and in a flat town the DEM has none of it. The Leszno viaduct's west end is
 * 18 m from the street it crosses, which no ramp inside the bridge can climb. The ramp
 * stops sooner at the first junction, because a side street meeting a ramp halfway up has
 * nothing to meet.
 */
const APPROACH_METRES = 200;

/**
 * A road under a deck within this of parallel to it — the sine of the angle between them —
 * is running beside it rather than under it. sin 25°.
 */
const ALONGSIDE = 0.42;

/**
 * Station spacing along a deck. Fine enough that a ramp drawn from the stations and a ramp
 * evaluated between them agree to a centimetre, and that the narrowest channel any of the
 * fixtures contains is found.
 */
const DECK_STEP = 4;

/**
 * How far above the height it is already at a deck may be before a car is deemed to be
 * *under* it rather than on it.
 *
 * Generous, because it is only ever guarding against a step no legitimate approach takes: a
 * deck meets its abutment at the height of the street there, and the steepest ramp gains a
 * couple of centimetres per 1/120 s substep at full speed. A metre is far more than either
 * and far less than the headroom under any bridge worth driving under.
 */
const DECK_STEP_LIMIT = 1;

/**
 * The wall along the edge of a deck: how tall above the deck, and how thick.
 *
 * A deck used to end at the back of its pavement and nothing else, and on the Leszno viaduct
 * that was a six-metre drop onto the street below for any car that ran a little wide. A metre
 * is a Polish road-bridge parapet, and it is low enough that the chase camera still sees the
 * street underneath over it.
 */
export const PARAPET_HEIGHT = 1;
export const PARAPET_THICKNESS = 0.3;

/**
 * Less than this under the edge of a deck, and it is not worth a wall.
 *
 * Every deck meets its abutments at the height of the street there, so without this a
 * parapet would run right down the ramp to the ground and stand across the pavement of
 * whatever the bridge lands on.
 */
const PARAPET_DROP = 1;

/**
 * How far past the edge to look for the surface carrying on, and how close in height it has
 * to be to count.
 *
 * A deck's edge is only an edge where nothing continues it. Where another member of the
 * structure joins — a slip road, the other arm of a junction on top, the second carriageway
 * of a dual carriageway — the ground beyond the edge is the next deck, and a wall there would
 * close the junction off.
 *
 * Close in height means a step the car can take: no more than `DECK_STEP_LIMIT`, the most the
 * physics will climb onto a deck. It was a metre and a half, to keep the median of the Leszno
 * viaduct open while its two halves stood that far apart. A car crossing up that step was
 * refused the higher deck and put on the street underneath. The halves are levelled now (see
 * `levelAlongside`), so anything still that far apart is a real step, and it gets a wall.
 */
const PARAPET_PROBE = 0.6;
const PARAPET_LEVEL = DECK_STEP_LIMIT;

/**
 * How far below a parapet's foot a car can be and still hit it.
 *
 * A car on the deck is at the deck's height where it is, and the parapet at the deck's height
 * at the edge beside it. Those agree to a few centimetres, but a car meeting the wall on a
 * ramp is a station or two along from where the wall's height was taken. A metre covers that
 * with room to spare, and is still nowhere near anything driving under the deck.
 */
const PARAPET_REACH_BELOW = 1;

/** One run of wall along a deck edge, as the mesh draws it and the physics hits it. */
export interface Parapet {
  /** The way whose deck it stands on, so the mesh can stand it on that way's pavement. */
  wayId: number;
  /** The outer face, along the deck edge, as x,z pairs. */
  outer: number[];
  /** The inner face, `PARAPET_THICKNESS` in from the edge, as x,z pairs. */
  inner: number[];
  /** Deck surface height at each point. */
  y: number[];
}

interface ParapetSegment {
  ax: number;
  az: number;
  bx: number;
  bz: number;
  ya: number;
  yb: number;
  /** Unit normal pointing back onto the deck. */
  nx: number;
  nz: number;
}

/** Push-out for a car circle against a parapet, in the same terms as `BuildingIndex`'s. */
export interface ParapetHit {
  px: number;
  pz: number;
  nx: number;
  nz: number;
  depth: number;
}

/** The bare edge a deck gets beyond the carriageway where there is no kerb. */
const DECK_SHOULDER = 0.3;

/**
 * Out to the outer edge of what is drawn on a deck: carriageway, kerb and pavement.
 *
 * The physics uses the same width. A deck used to be indexed by its carriageway alone, so a
 * car on the pavement of a bridge fell to the ground under it — harmless at the two metres
 * a river crossing was lifted, and a six-metre drop off a viaduct onto the street beneath.
 */
export function deckHalfWidth(road: Road): number {
  // The wider side, so a deck whose one pavement a mapped sidewalk widened is that wide on
  // both. A roundabout has its kerb and no pavement.
  const reach = Math.max(...pavementReach(road));
  if (reach > 0) return reach;
  const kerbed = ROAD_SPECS[road.cls].drivable && road.width >= KERB_MIN_ROAD_WIDTH;
  return road.width / 2 + (kerbed ? KERB_STONE_WIDTH : DECK_SHOULDER);
}

/**
 * A run of road with a surface the ground does not decide: a stretch of bridge, a tunnel,
 * or the end of a street ramping up to a bridge.
 *
 * `h` is parallel to `pts`. For a raised approach it is a lift on top of the ground rather
 * than a height, so the street keeps following the hill it is on and only the embankment is
 * added.
 */
interface Piece {
  wayId: number;
  pts: number[];
  h: Float64Array;
  raised: boolean;
  tunnel: boolean;
  half: number;
}

/** A deck found at a point: its nearest segment there, how far off its centreline, its height. */
interface PieceHit {
  s: Segment;
  d2: number;
  y: number;
}

interface Segment {
  piece: number;
  /** Station index of the segment's start in its piece. */
  i: number;
  ax: number;
  az: number;
  bx: number;
  bz: number;
  half2: number;
}

/**
 * Every deck in a world, and a way to ask which one a point is on.
 *
 * Heights are worked out once, at stations every few metres, and everything else — the mesh,
 * the physics, the route line — interpolates between the same stations. Nothing evaluates
 * the shape of a ramp for itself, so nothing can disagree about it.
 */
export class DeckIndex {
  private readonly pieces: Piece[] = [];
  private readonly segments: Segment[] = [];
  private readonly grid = new Map<number, number[]>();
  private readonly byWay = new Map<number, number[]>();
  private readonly terrain: Heightfield;
  private static readonly CELL = 32;

  /** Every parapet in the world, for the mesh. */
  readonly parapets: Parapet[] = [];
  private readonly walls: ParapetSegment[] = [];
  private readonly wallGrid = new Map<number, number[]>();

  /** True when there is nothing to look up, so callers can skip the work entirely. */
  readonly isEmpty: boolean;

  private constructor(world: World, terrain: Heightfield) {
    this.terrain = terrain;
    if (terrain.isFlat) {
      this.isEmpty = true;
      return;
    }

    this.pieces = [...buildBridges(world, terrain), ...buildTunnels(world, terrain)];
    for (let p = 0; p < this.pieces.length; p++) {
      const piece = this.pieces[p];
      const list = this.byWay.get(piece.wayId);
      if (list) list.push(p);
      else this.byWay.set(piece.wayId, [p]);
      const half2 = piece.half * piece.half;
      for (let i = 2; i < piece.pts.length; i += 2) {
        const index = this.segments.length;
        const segment: Segment = {
          piece: p,
          i: i / 2 - 1,
          ax: piece.pts[i - 2],
          az: piece.pts[i - 1],
          bx: piece.pts[i],
          bz: piece.pts[i + 1],
          half2,
        };
        this.segments.push(segment);
        this.insert(index, segment, piece.half);
      }
    }

    this.isEmpty = this.segments.length === 0;
    if (!this.isEmpty) this.buildParapets();
  }

  static build(world: World, terrain: Heightfield): DeckIndex {
    return new DeckIndex(world, terrain);
  }

  /**
   * The surface of one bridge or tunnel way, for the mesh builder — which knows which way it
   * is drawing and does not need the spatial lookup. Null for a way that is not a deck,
   * including a street that merely ramps up to one: see `liftFor`.
   */
  profileFor(wayId: number): DeckProfile | null {
    const pieces = this.piecesOf(wayId, false);
    if (!pieces.length) return null;
    return (x, z) => this.nearestOn(pieces, x, z);
  }

  /** All of them at once, keyed by way id, for a builder that walks every road in turn. */
  profileMap(): Map<number, DeckProfile> {
    const out = new Map<number, DeckProfile>();
    for (const id of this.byWay.keys()) {
      const profile = this.profileFor(id);
      if (profile) out.set(id, profile);
    }
    return out;
  }

  /**
   * How far a street is lifted off the ground on its way up to a bridge, or null for one
   * that is not.
   *
   * Zero beyond the end of the ramp, which is where the station nearest any point past it
   * says it is, so a way that is a ramp for its first eighty metres and a street for the
   * rest can be asked about anywhere along its length.
   */
  liftFor(wayId: number): DeckProfile | null {
    const pieces = this.piecesOf(wayId, true);
    if (!pieces.length) return null;
    return (x, z) => {
      // A short street between two bridges is a ramp from both ends; the higher one wins.
      let lift = 0;
      for (const p of pieces) lift = Math.max(lift, this.nearestOn([p], x, z));
      return lift;
    };
  }

  liftMap(): Map<number, DeckProfile> {
    const out = new Map<number, DeckProfile>();
    for (const id of this.byWay.keys()) {
      const lift = this.liftFor(id);
      if (lift) out.set(id, lift);
    }
    return out;
  }

  /**
   * The deck under a point, or null where there is none.
   *
   * Nearest by centreline distance rather than first hit, so where two decks overlap the car
   * is put on the one it is actually driving along.
   *
   * `fromY` is what stops a bridge claiming the street *underneath* it. A deck is indexed by
   * its ribbon, and a road crossing under one is inside that ribbon by definition — without a
   * height test, driving the quay under Pont Marie teleported the car three metres up onto
   * the bridge. Passing the height the car is already at means a deck is only yours if you
   * arrived along it: the abutment matches the street it opens onto, so driving on is free,
   * and the ramp gains at most a few centimetres per substep. Omit it — as the spawn does —
   * to ask what is on top at a point, with nothing to come from.
   *
   * With it, the car stays on the nearest deck within a step of where it is, up or down. One
   * further off does not end the search, because it may be over the deck the car is on: a
   * slip road climbing out from under the edge of the viaduct it joins is inside the viaduct's
   * footprint all the way up, and giving up on the nearer, higher deck dropped the car through
   * its own road. With nothing within a step, the car is on the ground if the ground is within
   * one, and otherwise falling, onto the highest thing below it.
   */
  sample(x: number, z: number, fromY?: number): DeckSample | null {
    if (this.isEmpty) return null;
    let best: PieceHit | null = null;
    let below: PieceHit | null = null;
    for (const hit of this.piecesAt(x, z)) {
      const piece = this.pieces[hit.s.piece];
      // A tunnel clipped to the ground is following the ground, and the ground's own gradient
      // is the one the car should feel there.
      if (piece.tunnel && hit.y >= this.terrain.heightAt(x, z) - 1e-6) continue;
      if (fromY !== undefined && Math.abs(hit.y - fromY) > DECK_STEP_LIMIT) {
        if (hit.y < fromY && (!below || hit.y > below.y)) below = hit;
        continue;
      }
      if (!best || hit.d2 < best.d2) best = hit;
    }
    if (!best && below) {
      const ground = this.terrain.heightAt(x, z);
      if (Math.abs(ground - fromY!) > DECK_STEP_LIMIT && below.y > ground) best = below;
    }
    if (!best) return null;

    const { s: seg, y } = best;
    const piece = this.pieces[seg.piece];
    const len = Math.hypot(seg.bx - seg.ax, seg.bz - seg.az);
    if (len < 1e-6) return { y, gradeX: 0, gradeZ: 0 };
    const ux = (seg.bx - seg.ax) / len;
    const uz = (seg.bz - seg.az) / len;
    const along = (piece.h[seg.i + 1] - piece.h[seg.i]) / len;
    if (!piece.raised) return { y, gradeX: along * ux, gradeZ: along * uz };
    // A ramp on a hillside climbs the hill and the embankment at once.
    const hill = this.terrain.slopeAt(x, z);
    return { y, gradeX: hill.dx + along * ux, gradeZ: hill.dz + along * uz };
  }

  /**
   * The lowest deck surface over a point that is higher than `above`, or null where there is
   * none: what a railway's overhead line has to pass under. Every deck counts, not just the
   * nearest, because the track may be on a bridge of its own under the road. `pad` widens each
   * deck, for a mast that has to keep clear of the edge as well as stay out from under it.
   */
  ceilingAt(x: number, z: number, above: number, pad = 0): number | null {
    let best: number | null = null;
    for (const hit of this.piecesAt(x, z, pad)) {
      if (this.pieces[hit.s.piece].tunnel) continue;
      if (hit.y > above && (best === null || hit.y < best)) best = hit.y;
    }
    return best;
  }

  /**
   * Every deck within its own half-width (plus `pad`) of a point, each at the nearest point on
   * its centreline, and the height there.
   *
   * One answer per deck, not per segment. Each segment of a deck covers the round-ended strip
   * around it, so past the end of a segment its last station's height still applies. The next
   * segment gives the right height there, and it is the nearer, so taking only the nearest
   * segment of each deck keeps the right one. Taking every segment brought back heights from
   * further along a ramp. Ten metres down from where a ramp meets its street, the stations
   * half a metre up still counted as the road carrying on beside a car a metre and a half up.
   */
  private piecesAt(x: number, z: number, pad = 0): PieceHit[] {
    if (this.isEmpty) return [];
    const cell = DeckIndex.CELL;
    const nearest = new Map<number, { s: Segment; t: number; d2: number }>();
    // One cell is the physics' every step; a segment is only ever in a cell once.
    const seen = pad > 0 ? new Set<number>() : null;
    for (let cx = Math.floor((x - pad) / cell); cx <= Math.floor((x + pad) / cell); cx++) {
      for (let cz = Math.floor((z - pad) / cell); cz <= Math.floor((z + pad) / cell); cz++) {
        for (const index of this.grid.get(cx * 100003 + cz) ?? []) {
          if (seen) {
            if (seen.has(index)) continue;
            seen.add(index);
          }
          const s = this.segments[index];
          const t = projectT(s, x, z);
          const d2 = (x - (s.ax + (s.bx - s.ax) * t)) ** 2 + (z - (s.az + (s.bz - s.az) * t)) ** 2;
          const known = nearest.get(s.piece);
          if (!known || d2 < known.d2) nearest.set(s.piece, { s, t, d2 });
        }
      }
    }
    const out: PieceHit[] = [];
    for (const { s, t, d2 } of nearest.values()) {
      const piece = this.pieces[s.piece];
      if (d2 > (piece.half + pad) ** 2) continue;
      const y = (piece.raised ? this.terrain.heightAt(x, z) : 0) + lerp(piece.h[s.i], piece.h[s.i + 1], t);
      out.push({ s, d2, y });
    }
    return out;
  }

  /**
   * Resolves a circle against the parapets at the height it is at.
   *
   * The height is the whole point. A parapet stands on a deck, and a deck stands over a
   * street, so in plan every car driving under the Leszno viaduct crosses its parapets twice.
   * Only a car up at the level of the deck meets one, and only from the deck side: a car whose
   * centre is already past the wall has gone over it, and pushing it back up onto the bridge
   * from the outside would be a teleport.
   */
  resolveCircle(x: number, z: number, radius: number, y: number): ParapetHit | null {
    if (!this.walls.length) return null;
    const cell = DeckIndex.CELL;
    let px = 0;
    let pz = 0;
    let nx = 0;
    let nz = 0;
    let depth = 0;
    let hits = 0;
    const seen = new Set<number>();
    for (let cx = Math.floor((x - radius) / cell); cx <= Math.floor((x + radius) / cell); cx++) {
      for (let cz = Math.floor((z - radius) / cell); cz <= Math.floor((z + radius) / cell); cz++) {
        const bucket = this.wallGrid.get(cx * 100003 + cz);
        if (!bucket) continue;
        for (const index of bucket) {
          if (seen.has(index)) continue;
          seen.add(index);
          const w = this.walls[index];
          const t = projectT(w, x, z);
          const foot = lerp(w.ya, w.yb, t);
          if (y < foot - PARAPET_REACH_BELOW || y > foot + PARAPET_HEIGHT) continue;
          const hx = w.ax + (w.bx - w.ax) * t;
          const hz = w.az + (w.bz - w.az) * t;
          if ((x - hx) * w.nx + (z - hz) * w.nz < 0) continue;
          const dist = Math.hypot(x - hx, z - hz);
          if (dist >= radius) continue;
          const dirX = dist > 1e-5 ? (x - hx) / dist : w.nx;
          const dirZ = dist > 1e-5 ? (z - hz) / dist : w.nz;
          const pen = radius - dist;
          px += dirX * pen;
          pz += dirZ * pen;
          nx += w.nx;
          nz += w.nz;
          if (pen > depth) depth = pen;
          hits++;
        }
      }
    }
    if (!hits) return null;
    const nLen = Math.hypot(nx, nz) || 1;
    return { px: px / hits, pz: pz / hits, nx: nx / nLen, nz: nz / nLen, depth };
  }

  /**
   * Walls along every deck edge with a drop beyond it and nothing carrying the surface on.
   *
   * Worked out from the deck's own stations, offset by the same mitred ribbon the mesh draws
   * the edge with, so the wall a car hits is the wall it can see — on the curve of a slip road
   * as much as on the straight.
   */
  private buildParapets(): void {
    for (const piece of this.pieces) {
      if (piece.tunnel) continue;
      const n = piece.pts.length / 2;
      if (n < 2) continue;
      const surface = (i: number) =>
        (piece.raised ? this.terrain.heightAt(piece.pts[i * 2], piece.pts[i * 2 + 1]) : 0) + piece.h[i];
      const edge = ribbon(piece.pts, piece.half * 2).positions;
      const probe = ribbon(piece.pts, (piece.half + PARAPET_PROBE) * 2).positions;
      const inner = ribbon(piece.pts, (piece.half - PARAPET_THICKNESS) * 2).positions;
      // `ribbon` drops repeated points, and then its stations no longer line up with ours.
      if (edge.length !== n * 6) continue;

      for (const side of [0, 1]) {
        // A point on the edge, `f` of the way from station `i` to the next.
        const at = (i: number, f: number) => {
          const j = Math.min(n - 1, i + 1);
          const o = side * 3;
          const mix = (a: number[], k: number) => lerp(a[i * 6 + o + k], a[j * 6 + o + k], f);
          return {
            ox: mix(edge, 0),
            oz: mix(edge, 2),
            ix: mix(inner, 0),
            iz: mix(inner, 2),
            qx: mix(probe, 0),
            qz: mix(probe, 2),
            y: lerp(surface(i), surface(j), f),
          };
        };
        // Carrying on means from the very edge, not just at the probe: across a slot between two
        // decks the car's centre has nothing under it, and falls.
        const edgeX = (p: { ox: number; qx: number }) => p.ox + (p.qx - p.ox) * (0.05 / PARAPET_PROBE);
        const edgeZ = (p: { oz: number; qz: number }) => p.oz + (p.qz - p.oz) * (0.05 / PARAPET_PROBE);
        const guards = (p: ReturnType<typeof at>) =>
          p.y - this.terrain.heightAt(p.qx, p.qz) > PARAPET_DROP &&
          !(this.surfaceNear(p.qx, p.qz, p.y) && this.surfaceNear(edgeX(p), edgeZ(p), p.y));
        // Where between two stations the wall stops being needed. Stations are four metres
        // apart, and a wall that stopped at the last one would leave up to that much open at
        // every corner where a slip road leaves the deck: the deck's own wall and the slip
        // road's would each end a station short of where the two edges meet.
        const boundary = (i: number, guardedAtStart: boolean) => {
          let lo = 0;
          let hi = 1;
          for (let k = 0; k < 7; k++) {
            const mid = (lo + hi) / 2;
            if (guards(at(i, mid)) === guardedAtStart) lo = mid;
            else hi = mid;
          }
          return at(i, guardedAtStart ? lo : hi);
        };

        const guarded = Array.from({ length: n }, (_, i) => guards(at(i, 0)));
        for (let i = 0; i < n; i++) {
          if (!guarded[i] || (i > 0 && guarded[i - 1])) continue;
          let end = i;
          while (end + 1 < n && guarded[end + 1]) end++;
          const run: ReturnType<typeof at>[] = [];
          const add = (p: ReturnType<typeof at>) => {
            const last = run[run.length - 1];
            if (!last || Math.hypot(p.ox - last.ox, p.oz - last.oz) > 0.01) run.push(p);
          };
          if (i > 0) add(boundary(i - 1, false));
          for (let k = i; k <= end; k++) add(at(k, 0));
          if (end < n - 1) add(boundary(end, true));
          if (run.length >= 2) this.addParapet(piece.wayId, run);
        }
      }
    }
  }

  private addParapet(wayId: number, run: { ox: number; oz: number; ix: number; iz: number; y: number }[]): void {
    const outer: number[] = [];
    const inner: number[] = [];
    const y: number[] = [];
    for (const p of run) {
      outer.push(p.ox, p.oz);
      inner.push(p.ix, p.iz);
      y.push(p.y);
    }
    this.parapets.push({ wayId, outer, inner, y });

    for (let k = 1; k < run.length; k++) {
      const ax = inner[(k - 1) * 2];
      const az = inner[(k - 1) * 2 + 1];
      const bx = inner[k * 2];
      const bz = inner[k * 2 + 1];
      // Back onto the deck is from the outer face towards the inner one.
      let nx = (ax + bx - outer[(k - 1) * 2] - outer[k * 2]) / 2;
      let nz = (az + bz - outer[(k - 1) * 2 + 1] - outer[k * 2 + 1]) / 2;
      const len = Math.hypot(nx, nz);
      if (len < 1e-6) continue;
      nx /= len;
      nz /= len;
      const index = this.walls.length;
      this.walls.push({ ax, az, bx, bz, ya: y[k - 1], yb: y[k], nx, nz });
      const cell = DeckIndex.CELL;
      for (let cx = Math.floor(Math.min(ax, bx) / cell); cx <= Math.floor(Math.max(ax, bx) / cell); cx++) {
        for (let cz = Math.floor(Math.min(az, bz) / cell); cz <= Math.floor(Math.max(az, bz) / cell); cz++) {
          const key = cx * 100003 + cz;
          const bucket = this.wallGrid.get(key);
          if (bucket) bucket.push(index);
          else this.wallGrid.set(key, [index]);
        }
      }
    }
  }

  /** Whether some deck covers a point at about this height — the surface carrying on. */
  private surfaceNear(x: number, z: number, y: number): boolean {
    return this.piecesAt(x, z).some((hit) => !this.pieces[hit.s.piece].tunnel && Math.abs(hit.y - y) <= PARAPET_LEVEL);
  }

  private piecesOf(wayId: number, raised: boolean): Piece[] {
    return (this.byWay.get(wayId) ?? []).map((p) => this.pieces[p]).filter((p) => p.raised === raised);
  }

  /** Height (or lift) at the station nearest a point, interpolated along its segment. */
  private nearestOn(pieces: Piece[], x: number, z: number): number {
    let best = Infinity;
    let value = 0;
    for (const piece of pieces) {
      const pts = piece.pts;
      if (pts.length < 4) continue;
      for (let i = 2; i < pts.length; i += 2) {
        const s = { ax: pts[i - 2], az: pts[i - 1], bx: pts[i], bz: pts[i + 1] };
        const t = projectT(s, x, z);
        const d2 = (x - (s.ax + (s.bx - s.ax) * t)) ** 2 + (z - (s.az + (s.bz - s.az) * t)) ** 2;
        if (d2 >= best) continue;
        best = d2;
        value = lerp(piece.h[i / 2 - 1], piece.h[i / 2], t);
      }
    }
    return value;
  }

  private insert(index: number, s: Segment, pad: number): void {
    const cell = DeckIndex.CELL;
    const minX = Math.floor((Math.min(s.ax, s.bx) - pad) / cell);
    const maxX = Math.floor((Math.max(s.ax, s.bx) + pad) / cell);
    const minZ = Math.floor((Math.min(s.az, s.bz) - pad) / cell);
    const maxZ = Math.floor((Math.max(s.az, s.bz) + pad) / cell);
    for (let cx = minX; cx <= maxX; cx++) {
      for (let cz = minZ; cz <= maxZ; cz++) {
        const key = cx * 100003 + cz;
        const bucket = this.grid.get(key);
        if (bucket) bucket.push(index);
        else this.grid.set(key, [index]);
      }
    }
  }
}

// ---------------------------------------------------------------------------
// bridges
// ---------------------------------------------------------------------------

/** One vertex of a structure's graph: an OSM node, or a clipped end that has none. */
interface Vertex {
  x: number;
  z: number;
  edges: { to: number; length: number }[];
}

/** Something the deck has to clear, at one station, and how high it needs the deck. */
interface Obstacle {
  station: number;
  need: number;
  water: boolean;
}

/** Where one end of a structure meets the ground, and how its ramp is laid out. */
interface Anchor {
  vertex: number;
  height: number;
  /** Geodesic distance from this anchor to every vertex of the structure. */
  dist: Float64Array;
  approach: { road: number; pts: number[]; s: number[] }[];
  approachLength: number;
  /** The ramp, in metres of distance from the anchor; negative is out along the approach. */
  rampStart: number;
  rampLength: number;
}

/**
 * Stations along every member of a structure, with each station's distance from every
 * anchor, so the base height and the ramps can be read off without walking the graph again.
 */
interface Station {
  x: number;
  z: number;
  road: number;
  d: number[];
}

/**
 * Every bridge in the world, structure by structure, lowest first.
 *
 * Lowest first because a bridge over a bridge has to clear the deck of the lower one, not
 * the ground under both of them, and the lower one's height is only known once it has been
 * built.
 */
function buildBridges(world: World, terrain: Heightfield): Piece[] {
  const roads = [...world.roads, ...railDeckRoads(world)];
  /** Indices from here on are railway. */
  const railFrom = world.roads.length;
  const water = new WaterIndex(world.areas);
  const levels = waterLevels(world.areas, terrain);
  const byNode = new Map<number, number[]>();
  for (let r = 0; r < roads.length; r++) {
    for (const node of roads[r].nodes) {
      if (node < 0) continue;
      const list = byNode.get(node);
      if (list) {
        if (list[list.length - 1] !== r) list.push(r);
      } else byNode.set(node, [r]);
    }
  }

  const wired = new Set((world.rail?.lines ?? []).filter((l) => l.electrified).map((l) => l.id));
  const structures = groupBridges(roads, byNode);
  structures.sort((a, b) => layerOf(roads, a) - layerOf(roads, b) || roads[a[0]].id - roads[b[0]].id);

  const under = new RoadGrid(roads);
  const built = new Map<number, Piece>();
  const pieces: Piece[] = [];
  for (const members of structures) {
    const out = buildStructure(members, roads, byNode, terrain, water, levels, under, built, railFrom, wired);
    pieces.push(...out);
  }
  // A railway bridge beside a road bridge keeps the height its track was given.
  const railways = new Set(roads.slice(railFrom).map((r) => r.id));
  levelAlongside(pieces.filter((p) => !railways.has(p.wayId)), terrain);
  return pieces;
}

/**
 * How far apart two decks running side by side may be, edge to edge, and still be levelled
 * with each other. Further apart than that each keeps its own height and its own wall, and a
 * metre of air between two walls reads as two roads.
 */
const LEVEL_GAP = 1;

/** More than this apart in height and two decks are stacked, not side by side. */
const LEVEL_LIMIT = 2.5;

/**
 * The steepest the lift added by levelling may change along a road: the average gradient of
 * a ramp, so the extra climb onto a levelled half reads as part of the ramp it was put on.
 */
const LEVEL_TAPER = RAMP_GRADE;

/**
 * Brings decks that run side by side to one height: the two halves of a dual carriageway,
 * above all.
 *
 * Each half is its own structure, because nothing joins them through a node, and each is
 * lifted from its own abutments over whatever it alone crosses. On the Leszno viaduct that
 * left them up to 1.3 m apart along the ramps, so the deck read as two slabs stacked
 * unevenly. It also left the median open, because the other half was close enough in height
 * to count as the road carrying on. A car crossing it from the lower half met a step too high
 * to be the road it was on, so the physics put it on the street six metres below.
 *
 * The lower deck is raised to the higher one, never the other way, so neither loses the
 * clearance it was built for. The raise is spread along the network the decks form, at no
 * more than a ramp's gradient each way, so it never makes a step where one piece of deck
 * meets the next. It comes back to nothing at every end that meets the ground, so no ramp is
 * left in the air. Where that leaves the halves apart — one ramp reaching the ground well
 * before the other — the parapet test still sees the step and walls it.
 */
function levelAlongside(pieces: Piece[], terrain: Heightfield): void {
  const decks = pieces.filter((p) => !p.tunnel && p.pts.length >= 4);
  if (decks.length < 2) return;

  const offset: number[] = [];
  let count = 0;
  for (const p of decks) {
    offset.push(count);
    count += p.pts.length / 2;
  }
  const surface = (p: Piece, i: number) =>
    (p.raised ? terrain.heightAt(p.pts[i * 2], p.pts[i * 2 + 1]) : 0) + p.h[i];

  // Stations of different pieces at the same point and height are one place on the network:
  // where a deck meets its ramp, and every node of a junction on top. The height keeps a deck
  // that happens to have a station right over one of another deck's apart from it.
  const at = new Map<string, { station: number; y: number }[]>();
  for (let d = 0; d < decks.length; d++) {
    for (let i = 0; i < decks[d].pts.length / 2; i++) {
      const key = `${Math.round(decks[d].pts[i * 2] * 10)},${Math.round(decks[d].pts[i * 2 + 1] * 10)}`;
      const list = at.get(key);
      const entry = { station: offset[d] + i, y: surface(decks[d], i) };
      if (list) list.push(entry);
      else at.set(key, [entry]);
    }
  }
  const groups: number[][] = [];
  const joined = new Uint8Array(count);
  for (const list of at.values()) {
    for (const a of list) {
      if (joined[a.station]) continue;
      const group = list.filter((b) => !joined[b.station] && Math.abs(b.y - a.y) < 0.5).map((b) => b.station);
      if (group.length < 2) continue;
      for (const s of group) joined[s] = 1;
      groups.push(group);
    }
  }

  // Along the network from every end that meets the ground — a ramp's foot, or a deck whose
  // abutment is a junction at street level — which is as far as a raise may reach back.
  const reach = new Float64Array(count).fill(Infinity);
  for (let d = 0; d < decks.length; d++) {
    for (const i of [0, decks[d].pts.length / 2 - 1]) if (!joined[offset[d] + i]) reach[offset[d] + i] = 0;
  }
  spread(decks, offset, groups, reach, 1, Math.min);

  const segments = new SegmentGrid(decks);
  for (let pass = 0; pass < 2; pass++) {
    const raise = new Float64Array(count);
    for (let d = 0; d < decks.length; d++) {
      const a = decks[d];
      const n = a.pts.length / 2;
      for (let i = 0; i < n; i++) {
        const x = a.pts[i * 2];
        const z = a.pts[i * 2 + 1];
        const p = Math.max(0, i - 1);
        const q = Math.min(n - 1, i + 1);
        const len = Math.hypot(a.pts[q * 2] - a.pts[p * 2], a.pts[q * 2 + 1] - a.pts[p * 2 + 1]);
        if (len < 1e-6) continue;
        const ux = (a.pts[q * 2] - a.pts[p * 2]) / len;
        const uz = (a.pts[q * 2 + 1] - a.pts[p * 2 + 1]) / len;
        const here = surface(a, i);
        let need = 0;
        for (const s of segments.near(x, z, a.half + LEVEL_GAP)) {
          const b = decks[s.piece];
          if (b.wayId === a.wayId) continue;
          // Beside it, not before its start or past its end: the next piece of the same road,
          // further up the same ramp, is within reach of the last stations of this one.
          const t = ((x - s.ax) * s.ux + (z - s.az) * s.uz) / s.length;
          if (t < 0 || t > 1) continue;
          const gap = Math.hypot(x - (s.ax + (s.bx - s.ax) * t), z - (s.az + (s.bz - s.az) * t));
          if (gap > a.half + b.half + LEVEL_GAP) continue;
          // Crossing over or under rather than running beside.
          if (Math.abs(ux * s.uz - uz * s.ux) > ALONGSIDE) continue;
          const there = lerp(surface(b, s.i), surface(b, s.i + 1), t);
          if (there - here > LEVEL_LIMIT) continue;
          need = Math.max(need, there - here);
        }
        raise[offset[d] + i] = need;
      }
    }
    spread(decks, offset, groups, raise, -LEVEL_TAPER, Math.max);
    let moved = false;
    for (let d = 0; d < decks.length; d++) {
      const p = decks[d];
      for (let i = 0; i < p.h.length; i++) {
        const lift = Math.min(raise[offset[d] + i], reach[offset[d] + i] * LEVEL_TAPER);
        if (lift > 1e-3) {
          p.h[i] += lift;
          moved = true;
        }
      }
    }
    if (!moved) break;
  }
}

/**
 * Carries a value along every piece and across the points pieces share, changing by `rate`
 * per metre, keeping whichever of the carried and the existing value `pick` prefers. A few
 * sweeps settle it: the network is a handful of chains joined at their ends.
 */
function spread(
  pieces: Piece[],
  offset: number[],
  groups: number[][],
  value: Float64Array,
  rate: number,
  pick: (a: number, b: number) => number,
): void {
  for (let sweep = 0; sweep < 8; sweep++) {
    let changed = false;
    for (let d = 0; d < pieces.length; d++) {
      const pts = pieces[d].pts;
      const n = pts.length / 2;
      for (const dir of [1, -1]) {
        for (let k = 1; k < n; k++) {
          const i = dir > 0 ? k : n - 1 - k;
          const j = i - dir;
          const step = Math.hypot(pts[i * 2] - pts[j * 2], pts[i * 2 + 1] - pts[j * 2 + 1]);
          const carried = value[offset[d] + j] + rate * step;
          const next = pick(value[offset[d] + i], carried);
          if (next !== value[offset[d] + i]) {
            value[offset[d] + i] = next;
            changed = true;
          }
        }
      }
    }
    for (const g of groups) {
      let best = value[g[0]];
      for (const s of g) best = pick(best, value[s]);
      for (const s of g) {
        if (value[s] !== best) {
          value[s] = best;
          changed = true;
        }
      }
    }
    if (!changed) break;
  }
}

/** The segments of a set of pieces on a grid, with the direction each runs in. */
class SegmentGrid {
  private readonly grid = new Map<number, number[]>();
  private readonly segs: {
    piece: number;
    i: number;
    ax: number;
    az: number;
    bx: number;
    bz: number;
    ux: number;
    uz: number;
    length: number;
  }[] = [];
  private readonly pad: number;
  private static readonly CELL = 32;

  constructor(pieces: Piece[]) {
    const cell = SegmentGrid.CELL;
    this.pad = Math.max(0, ...pieces.map((p) => p.half));
    for (let p = 0; p < pieces.length; p++) {
      const pts = pieces[p].pts;
      for (let i = 2; i < pts.length; i += 2) {
        const len = Math.hypot(pts[i] - pts[i - 2], pts[i + 1] - pts[i - 1]);
        if (len < 1e-6) continue;
        const index = this.segs.length;
        this.segs.push({
          piece: p,
          i: i / 2 - 1,
          ax: pts[i - 2],
          az: pts[i - 1],
          bx: pts[i],
          bz: pts[i + 1],
          ux: (pts[i] - pts[i - 2]) / len,
          uz: (pts[i + 1] - pts[i - 1]) / len,
          length: len,
        });
        for (let cx = Math.floor((Math.min(pts[i - 2], pts[i]) - this.pad) / cell); cx <= Math.floor((Math.max(pts[i - 2], pts[i]) + this.pad) / cell); cx++) {
          for (let cz = Math.floor((Math.min(pts[i - 1], pts[i + 1]) - this.pad) / cell); cz <= Math.floor((Math.max(pts[i - 1], pts[i + 1]) + this.pad) / cell); cz++) {
            const key = cx * 100003 + cz;
            const bucket = this.grid.get(key);
            if (bucket) bucket.push(index);
            else this.grid.set(key, [index]);
          }
        }
      }
    }
  }

  /** Segments that may come within `radius` plus the widest half of a point. */
  near(x: number, z: number, radius: number): SegmentGrid['segs'] {
    const cell = SegmentGrid.CELL;
    const out: SegmentGrid['segs'] = [];
    const seen = new Set<number>();
    for (let cx = Math.floor((x - radius) / cell); cx <= Math.floor((x + radius) / cell); cx++) {
      for (let cz = Math.floor((z - radius) / cell); cz <= Math.floor((z + radius) / cell); cz++) {
        for (const index of this.grid.get(cx * 100003 + cz) ?? []) {
          if (seen.has(index)) continue;
          seen.add(index);
          out.push(this.segs[index]);
        }
      }
    }
    return out;
  }
}

function layerOf(roads: Road[], members: number[]): number {
  let layer = -Infinity;
  for (const m of members) layer = Math.max(layer, roads[m].layer ?? 0);
  return layer;
}

/** Bridge ways joined through a shared node, or by being fragments of one way. */
function groupBridges(roads: Road[], byNode: Map<number, number[]>): number[][] {
  const parent = new Map<number, number>();
  const find = (a: number): number => {
    let root = a;
    while (parent.get(root)! !== root) root = parent.get(root)!;
    parent.set(a, root);
    return root;
  };
  const union = (a: number, b: number) => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent.set(Math.max(ra, rb), Math.min(ra, rb));
  };

  const firstOfWay = new Map<number, number>();
  for (let r = 0; r < roads.length; r++) {
    if (roads[r].deck !== 'bridge' || roads[r].pts.length < 4) continue;
    parent.set(r, r);
    const sibling = firstOfWay.get(roads[r].id);
    if (sibling === undefined) firstOfWay.set(roads[r].id, r);
    else union(r, sibling);
  }
  for (const [r] of parent) {
    for (const node of roads[r].nodes) {
      if (node < 0) continue;
      for (const other of byNode.get(node) ?? []) if (parent.has(other)) union(r, other);
    }
  }

  const groups = new Map<number, number[]>();
  for (const [r] of parent) {
    const root = find(r);
    const list = groups.get(root);
    if (list) list.push(r);
    else groups.set(root, [r]);
  }
  return [...groups.values()].map((g) => g.sort((a, b) => a - b));
}

function buildStructure(
  members: number[],
  roads: Road[],
  byNode: Map<number, number[]>,
  terrain: Heightfield,
  water: WaterIndex,
  levels: Map<number, number>,
  under: RoadGrid,
  built: Map<number, Piece>,
  railFrom: number,
  wired: Set<number>,
): Piece[] {
  const memberSet = new Set(members);
  const drivable = members.some((m) => ROAD_SPECS[roads[m].cls].drivable);
  const layer = layerOf(roads, members);
  const halfWidth = Math.max(...members.map((m) => deckHalfWidth(roads[m])));

  // --- the graph ----------------------------------------------------------
  const vertices: Vertex[] = [];
  const vertexKey = new Map<string, number>();
  /** Per member, the vertex index of each of its points. */
  const vertexOf = new Map<number, number[]>();
  const touching = new Set<number>();
  for (const m of members) {
    const road = roads[m];
    const ids: number[] = [];
    for (let i = 0; i < road.pts.length / 2; i++) {
      const node = road.nodes[i];
      if (node >= 0) for (const other of byNode.get(node) ?? []) touching.add(other);
      const key = node >= 0 ? `n${node}` : `c${road.pts[i * 2]}|${road.pts[i * 2 + 1]}`;
      let v = vertexKey.get(key);
      if (v === undefined) {
        v = vertices.length;
        vertices.push({ x: road.pts[i * 2], z: road.pts[i * 2 + 1], edges: [] });
        vertexKey.set(key, v);
      }
      if (ids.length) {
        const prev = ids[ids.length - 1];
        const length = Math.hypot(vertices[v].x - vertices[prev].x, vertices[v].z - vertices[prev].z);
        vertices[prev].edges.push({ to: v, length });
        vertices[v].edges.push({ to: prev, length });
      }
      ids.push(v);
    }
    vertexOf.set(m, ids);
  }

  // --- where it meets the ground ------------------------------------------
  //
  // A street joining it is an abutment; for a road bridge only a drivable one, because the
  // steps down from a viaduct to the pavement below join it mid-span and pinning the deck to
  // the ground there would fold it in half.
  const connects = (r: number) => !memberSet.has(r) && (!drivable || ROAD_SPECS[roads[r].cls].drivable);
  const anchorVertices = new Map<number, number[]>();
  const ends: number[] = [];
  for (const m of members) {
    const road = roads[m];
    const ids = vertexOf.get(m)!;
    for (let i = 0; i < ids.length; i++) {
      const node = road.nodes[i];
      const others = node >= 0 ? (byNode.get(node) ?? []).filter(connects) : [];
      if (others.length) anchorVertices.set(ids[i], others);
      const atEnd = i === 0 || i === ids.length - 1;
      const alone = node < 0 || (byNode.get(node) ?? []).every((r) => r === m);
      if (atEnd && alone) ends.push(ids[i]);
    }
  }
  // A free end — a clipped one at the edge of the region, or a way that just stops — meets
  // the ground too, at whatever height the ground is there.
  for (const v of ends) if (!anchorVertices.has(v)) anchorVertices.set(v, []);
  if (!anchorVertices.size) {
    for (const m of members) {
      const ids = vertexOf.get(m)!;
      anchorVertices.set(ids[0], []);
      anchorVertices.set(ids[ids.length - 1], []);
    }
  }

  const anchors: Anchor[] = [...anchorVertices.keys()]
    .sort((a, b) => a - b)
    .map((vertex) => ({
      vertex,
      height: terrain.heightAt(vertices[vertex].x, vertices[vertex].z),
      dist: shortestPaths(vertices, vertex),
      approach: [],
      approachLength: 0,
      rampStart: 0,
      rampLength: 0,
    }));

  // --- stations -------------------------------------------------------------
  const stations: Station[] = [];
  const stationsOf = new Map<number, number[]>();
  for (const m of members) {
    const road = roads[m];
    const ids = vertexOf.get(m)!;
    const list: number[] = [];
    for (let i = 0; i < ids.length; i++) {
      const x = road.pts[i * 2];
      const z = road.pts[i * 2 + 1];
      if (i === 0) {
        list.push(stations.length);
        stations.push({ x, z, road: m, d: anchors.map((a) => a.dist[ids[0]]) });
        continue;
      }
      const ax = road.pts[(i - 1) * 2];
      const az = road.pts[(i - 1) * 2 + 1];
      const length = Math.hypot(x - ax, z - az);
      const steps = Math.max(1, Math.ceil(length / DECK_STEP));
      for (let k = 1; k <= steps; k++) {
        const f = k / steps;
        list.push(stations.length);
        stations.push({
          x: ax + (x - ax) * f,
          z: az + (z - az) * f,
          road: m,
          d: anchors.map((a) =>
            Math.min(a.dist[ids[i - 1]] + f * length, a.dist[ids[i]] + (1 - f) * length),
          ),
        });
      }
    }
    stationsOf.set(m, list);
  }

  const base = stations.map((s) => baseHeight(anchors, s.d));

  // --- what it crosses --------------------------------------------------------
  const obstacles: Obstacle[] = [];
  /** Over water that runs downhill and so has no level to clear; see `FALLING_SHARE`. */
  let overStream = false;
  for (let k = 0; k < stations.length; k++) {
    const s = stations[k];
    const road = roads[s.road];
    const { nx, nz } = normalAt(stations, stationsOf.get(s.road)!, k);
    const half = road.width / 2;
    let need = -Infinity;
    let wet = false;
    // The ribbon's edges as well as its centreline: an approach span whose centreline stops
    // short of the river still has half its width out over it, and the two short links
    // either side of Pont d'Arcole were exactly that — dry by the centreline, 0.59 m under
    // water at the corners.
    for (const side of [0, 1, -1]) {
      const x = s.x + nx * half * side;
      const z = s.z + nz * half * side;
      const area = water.areaAt(x, z);
      const level = area ? levels.get(area.id) : undefined;
      if (area && level === undefined) overStream = true;
      if (level !== undefined && level + BRIDGE_CLEARANCE - base[k] > need) {
        need = level + BRIDGE_CLEARANCE - base[k];
        wet = true;
      }
      for (const { road: r, ux, uz } of under.covering(x, z)) {
        const other = roads[r];
        if (memberSet.has(r) || touching.has(r) || other.deck === 'tunnel') continue;
        // Something running alongside is beside the deck, not under it: the other half of a
        // dual carriageway climbs to its own viaduct a few metres away, and its ribbon
        // overlaps this one's all the way up the ramp.
        if (Math.abs(ux * nz - uz * nx) > ALONGSIDE) continue;
        if (other.deck === 'bridge' && (other.layer ?? 0) >= layer) continue;
        const below = built.get(r);
        const top = below ? valueNear(below, x, z) : terrain.heightAt(x, z);
        const headroom =
          r >= railFrom
            ? wired.has(other.id)
              ? WIRED_RAIL_HEADROOM
              : RAIL_HEADROOM
            : ROAD_SPECS[other.cls].drivable
              ? ROAD_HEADROOM
              : PATH_HEADROOM;
        if (top + headroom - base[k] > need) {
          need = top + headroom - base[k];
          wet = false;
        }
      }
    }
    if (need > 0) obstacles.push({ station: k, need, water: wet });
  }

  // Unless what it crosses is a stream drawn on the ground: then what it spans is known, and
  // it is level with the street either side.
  if (!obstacles.length && stations.length && !overStream) {
    // Nothing it crosses is in the payload, so the obstacle is put where a span is furthest
    // from either end — which is where a real one would be, and the only place the ramps can
    // reach a useful height on a short one. It is on the ground, so a bridge across a valley
    // already clears it and is left as the straight span it always was.
    let best = 0;
    let bestReach = -Infinity;
    for (let k = 0; k < stations.length; k++) {
      const reach = Math.min(...stations[k].d);
      if (reach > bestReach) {
        bestReach = reach;
        best = k;
      }
    }
    const s = stations[best];
    const need = terrain.heightAt(s.x, s.z) + UNSEEN_HEADROOM - base[best];
    if (need > 0) obstacles.push({ station: best, need, water: false });
  }

  const rise = Math.min(MAX_ARCH, Math.max(0, ...obstacles.map((o) => o.need)));

  // --- ramps ------------------------------------------------------------------
  if (rise > 0.01) {
    const comfortable = (1.5 * rise) / RAMP_GRADE;
    const steepest = (1.5 * rise) / STEEP_GRADE;
    anchors.forEach((anchor, a) => {
      // Short of each obstacle by the deck's own half-width, because a deck is a ribbon and
      // its centreline is the last part of it to arrive: without that margin the Marais put a
      // tongue of Seine over the foot of six bridges.
      let wet = Infinity;
      let dry = Infinity;
      for (const o of obstacles) {
        const d = Math.max(0, stations[o.station].d[a] - halfWidth);
        if (o.water) wet = Math.min(wet, d);
        else dry = Math.min(dry, d);
      }
      const connections = anchorVertices.get(anchor.vertex)!;
      if (connections.length === 1) {
        Object.assign(anchor, walkApproach(connections[0], anchor, vertices, roads, byNode, memberSet, drivable));
      }
      // The ramp ends where the first thing to clear begins, and starts as late as it can.
      const end = Math.min(wet, dry);
      const start = Math.max(-anchor.approachLength, end - comfortable);
      let length = end - start;
      if (length < steepest && dry < wet) {
        // Not enough room to climb over the street under the deck at any sane gradient. The
        // ramp runs on past it instead, and the lorry scrapes: a smaller lie than a wall in
        // the road. Never past water, though — over a river the clearance is not negotiable,
        // and where there is no dry approach at all (an abutment standing in the river, which
        // OSM's polygons produce constantly because they are drawn to the foot of the quay
        // wall and a little past it) the lift runs flat to the end rather than dipping the
        // deck into the Seine.
        length = Math.min(steepest, wet - start);
      }
      anchor.rampStart = start;
      anchor.rampLength = length;
    });
  }

  const liftAt = (d: number[]) => {
    if (rise <= 0.01) return 0;
    let shape = 1;
    for (let a = 0; a < anchors.length; a++) shape = Math.min(shape, rampShape(anchors[a], d[a]));
    return rise * shape;
  };

  // --- pieces -----------------------------------------------------------------
  const pieces: Piece[] = [];
  for (const m of members) {
    const list = stationsOf.get(m)!;
    const pts: number[] = [];
    const h = new Float64Array(list.length);
    list.forEach((k, i) => {
      pts.push(stations[k].x, stations[k].z);
      h[i] = base[k] + liftAt(stations[k].d);
    });
    const piece: Piece = { wayId: roads[m].id, pts, h, raised: false, tunnel: false, half: deckHalfWidth(roads[m]) };
    pieces.push(piece);
    built.set(m, piece);
  }
  for (const anchor of anchors) {
    if (rise <= 0.01) break;
    // The approach climbs to the deck as it is at this end, which is less than its own
    // ramp's full height when the far end's ramp has not finished coming down yet. On a 10 m
    // bridge in Leszno with a long street to the west and a junction just to the east, the
    // west approach climbed to 6 m and met a deck at 1.8: a cliff, and a car on the grass
    // beside it. Scaled rather than clipped, so the ramp stays a ramp all the way.
    const own = rise * rampShape(anchor, 0);
    const meets = liftAt(anchors.map((other) => other.dist[anchor.vertex]));
    const scale = own > 1e-6 ? Math.min(1, meets / own) : 0;
    for (const leg of anchor.approach) {
      const dense = densify(leg.pts);
      const pts: number[] = [];
      const lifts: number[] = [];
      let s = leg.s[0];
      for (let i = 0; i < dense.length; i += 2) {
        if (i) s += Math.hypot(dense[i] - dense[i - 2], dense[i + 1] - dense[i - 1]);
        const lift = rise * rampShape(anchor, -s) * scale;
        pts.push(dense[i], dense[i + 1]);
        lifts.push(lift);
        if (lift <= 0) break;
      }
      if (pts.length < 4) continue;
      pieces.push({
        wayId: roads[leg.road].id,
        pts,
        h: Float64Array.from(lifts),
        raised: true,
        tunnel: false,
        half: deckHalfWidth(roads[leg.road]),
      });
    }
  }
  return pieces;
}

/**
 * The fraction of the lift in force at a distance from one anchor: nothing before the ramp
 * starts, everything after it ends.
 *
 * Smoothstep rather than a straight ramp because the join is what a car drives over: a
 * linear ramp puts a kink in the deck at both ends of it, and the chase camera finds a kink
 * in a road surface immediately.
 */
function rampShape(anchor: Anchor, d: number): number {
  if (anchor.rampLength < 1) return 1;
  const u = (d - anchor.rampStart) / anchor.rampLength;
  const c = u < 0 ? 0 : u > 1 ? 1 : u;
  return c * c * (3 - 2 * c);
}

/**
 * The deck before any lift: the ground at every abutment, blended by how far along the
 * structure each one is.
 *
 * Inverse distance along the structure rather than a chord between two ends, because a
 * viaduct with a junction on it has three ends or four. With two it is exactly the straight
 * line between them, which is what every bridge was before — so a bridge whose abutments
 * already clear its river comes out where it always did.
 */
function baseHeight(anchors: Anchor[], d: number[]): number {
  let sum = 0;
  let weight = 0;
  for (let a = 0; a < anchors.length; a++) {
    if (d[a] < 1e-6) return anchors[a].height;
    if (!Number.isFinite(d[a])) continue;
    sum += anchors[a].height / d[a];
    weight += 1 / d[a];
  }
  return weight > 0 ? sum / weight : anchors[0]?.height ?? 0;
}

/**
 * Down the street from an abutment, as far as a ramp could use: to the first junction, the
 * edge of the region, another bridge, or `APPROACH_METRES`, whichever comes first.
 *
 * Only ever along a street that simply carries on — an abutment that is itself a junction
 * has no approach, because the side street would be left at the bottom of the ramp.
 */
function walkApproach(
  first: number,
  anchor: Anchor,
  vertices: Vertex[],
  roads: Road[],
  byNode: Map<number, number[]>,
  members: Set<number>,
  drivable: boolean,
): Pick<Anchor, 'approach' | 'approachLength'> {
  const legs: Anchor['approach'] = [];
  const start = vertices[anchor.vertex];
  let road = first;
  let x = start.x;
  let z = start.z;
  let travelled = 0;
  const counts = (r: number) => !drivable || ROAD_SPECS[roads[r].cls].drivable;
  const visited = new Set<number>();

  while (travelled < APPROACH_METRES && !visited.has(road)) {
    visited.add(road);
    const r = roads[road];
    if (r.deck) break;
    const n = r.pts.length / 2;
    const at = indexOfPoint(r.pts, x, z);
    if (at !== 0 && at !== n - 1) break;
    const step = at === 0 ? 1 : -1;
    const pts = [x, z];
    const s = [travelled];
    let i = at;
    let stop = false;
    let nextRoad = -1;
    while (i + step >= 0 && i + step < n) {
      i += step;
      const px = r.pts[i * 2];
      const pz = r.pts[i * 2 + 1];
      const length = Math.hypot(px - x, pz - z);
      if (travelled + length >= APPROACH_METRES) {
        const f = (APPROACH_METRES - travelled) / length;
        pts.push(x + (px - x) * f, z + (pz - z) * f);
        s.push(APPROACH_METRES);
        travelled = APPROACH_METRES;
        stop = true;
        break;
      }
      travelled += length;
      x = px;
      z = pz;
      pts.push(x, z);
      s.push(travelled);
      const node = r.nodes[i];
      if (node < 0) {
        stop = true;
        break;
      }
      const others = (byNode.get(node) ?? []).filter((o) => o !== road && counts(o));
      if (others.length > 1 || (others.length === 1 && (i + step >= 0 && i + step < n))) {
        // A side street, or a fork: this is where the ground has to be met.
        stop = true;
        break;
      }
      if (others.length === 1) nextRoad = others[0];
    }
    legs.push({ road, pts, s });
    if (stop || nextRoad < 0 || members.has(nextRoad)) break;
    road = nextRoad;
  }
  return { approach: legs, approachLength: travelled };
}

function indexOfPoint(pts: number[], x: number, z: number): number {
  for (let i = 0; i < pts.length; i += 2) if (pts[i] === x && pts[i + 1] === z) return i / 2;
  return -1;
}

/** Dijkstra over a structure's graph — a few dozen vertices, so a linear scan for the minimum. */
function shortestPaths(vertices: Vertex[], from: number): Float64Array {
  const dist = new Float64Array(vertices.length).fill(Infinity);
  const done = new Uint8Array(vertices.length);
  dist[from] = 0;
  for (;;) {
    let u = -1;
    for (let v = 0; v < vertices.length; v++) if (!done[v] && (u < 0 || dist[v] < dist[u])) u = v;
    if (u < 0 || !Number.isFinite(dist[u])) break;
    done[u] = 1;
    for (const e of vertices[u].edges) {
      if (dist[u] + e.length < dist[e.to]) dist[e.to] = dist[u] + e.length;
    }
  }
  return dist;
}

/** Unit normal to a member at one of its stations, from the stations either side. */
function normalAt(stations: Station[], list: number[], k: number): { nx: number; nz: number } {
  const at = list.indexOf(k);
  const a = stations[list[Math.max(0, at - 1)]];
  const b = stations[list[Math.min(list.length - 1, at + 1)]];
  const len = Math.hypot(b.x - a.x, b.z - a.z);
  if (len < 1e-6) return { nx: 0, nz: 0 };
  return { nx: -(b.z - a.z) / len, nz: (b.x - a.x) / len };
}

function valueNear(piece: Piece, x: number, z: number): number {
  let best = Infinity;
  let value = piece.h[0];
  for (let i = 2; i < piece.pts.length; i += 2) {
    const s = { ax: piece.pts[i - 2], az: piece.pts[i - 1], bx: piece.pts[i], bz: piece.pts[i + 1] };
    const t = projectT(s, x, z);
    const d2 = (x - (s.ax + (s.bx - s.ax) * t)) ** 2 + (z - (s.az + (s.bz - s.az) * t)) ** 2;
    if (d2 < best) {
      best = d2;
      value = lerp(piece.h[i / 2 - 1], piece.h[i / 2], t);
    }
  }
  return value;
}

/** Every road's carriageway on a grid, to ask what a deck passes over. */
class RoadGrid {
  private readonly grid = new Map<number, number[]>();
  private readonly segs: { road: number; ax: number; az: number; bx: number; bz: number; half2: number }[] = [];
  private static readonly CELL = 32;

  constructor(roads: Road[]) {
    const cell = RoadGrid.CELL;
    for (let r = 0; r < roads.length; r++) {
      const road = roads[r];
      const half = road.width / 2;
      for (let i = 2; i < road.pts.length; i += 2) {
        const index = this.segs.length;
        const s = { road: r, ax: road.pts[i - 2], az: road.pts[i - 1], bx: road.pts[i], bz: road.pts[i + 1], half2: half * half };
        this.segs.push(s);
        for (let cx = Math.floor((Math.min(s.ax, s.bx) - half) / cell); cx <= Math.floor((Math.max(s.ax, s.bx) + half) / cell); cx++) {
          for (let cz = Math.floor((Math.min(s.az, s.bz) - half) / cell); cz <= Math.floor((Math.max(s.az, s.bz) + half) / cell); cz++) {
            const key = cx * 100003 + cz;
            const bucket = this.grid.get(key);
            if (bucket) bucket.push(index);
            else this.grid.set(key, [index]);
          }
        }
      }
    }
  }

  /** Every carriageway covering a point, with the direction it runs in there. */
  covering(x: number, z: number): { road: number; ux: number; uz: number }[] {
    const cell = RoadGrid.CELL;
    const bucket = this.grid.get(Math.floor(x / cell) * 100003 + Math.floor(z / cell));
    if (!bucket) return [];
    const out: { road: number; ux: number; uz: number }[] = [];
    for (const index of bucket) {
      const s = this.segs[index];
      const t = projectT(s, x, z);
      const d2 = (x - (s.ax + (s.bx - s.ax) * t)) ** 2 + (z - (s.az + (s.bz - s.az) * t)) ** 2;
      if (d2 > s.half2) continue;
      const len = Math.hypot(s.bx - s.ax, s.bz - s.az) || 1;
      out.push({ road: s.road, ux: (s.bx - s.ax) / len, uz: (s.bz - s.az) / len });
    }
    return out;
  }
}

// ---------------------------------------------------------------------------
// tunnels
// ---------------------------------------------------------------------------

/**
 * A tunnel is the chord between its two furthest-apart portals with the ground taken as a
 * ceiling, so a portal still meets the street it opens onto but the bore stays inside the
 * hill.
 *
 * Keyed by way id, so a tunnel clipped into fragments by the region boundary is still one
 * chord.
 */
function buildTunnels(world: World, terrain: Heightfield): Piece[] {
  const ways = new Map<number, { fragments: number[][]; half: number }>();
  for (const road of world.roads) {
    if (road.deck !== 'tunnel' || road.pts.length < 4) continue;
    const entry = ways.get(road.id) ?? { fragments: [], half: 0 };
    entry.fragments.push(road.pts);
    entry.half = Math.max(entry.half, road.width * 0.5);
    ways.set(road.id, entry);
  }

  const pieces: Piece[] = [];
  for (const [id, { fragments, half }] of ways) {
    const ends: number[] = [];
    for (const pts of fragments) ends.push(pts[0], pts[1], pts[pts.length - 2], pts[pts.length - 1]);
    let ax = ends[0];
    let az = ends[1];
    let bx = ends[2];
    let bz = ends[3];
    let span = -1;
    for (let i = 0; i < ends.length; i += 2) {
      for (let j = i + 2; j < ends.length; j += 2) {
        const d = (ends[i] - ends[j]) ** 2 + (ends[i + 1] - ends[j + 1]) ** 2;
        if (d > span) {
          span = d;
          ax = ends[i];
          az = ends[i + 1];
          bx = ends[j];
          bz = ends[j + 1];
        }
      }
    }
    if (span < 1e-6) continue;
    const length = Math.sqrt(span);
    const ux = (bx - ax) / length;
    const uz = (bz - az) / length;
    const hA = terrain.heightAt(ax, az);
    const hB = terrain.heightAt(bx, bz);
    for (const fragment of fragments) {
      const pts = densify(fragment);
      const h = new Float64Array(pts.length / 2);
      for (let i = 0; i < h.length; i++) {
        const x = pts[i * 2];
        const z = pts[i * 2 + 1];
        const t = Math.min(1, Math.max(0, ((x - ax) * ux + (z - az) * uz) / length));
        h[i] = Math.min(hA + (hB - hA) * t, terrain.heightAt(x, z));
      }
      pieces.push({ wayId: id, pts, h, raised: false, tunnel: true, half });
    }
  }
  return pieces;
}

/** A polyline with extra points so no segment is longer than `DECK_STEP`, keeping every original. */
function densify(pts: number[]): number[] {
  const out = [pts[0], pts[1]];
  for (let i = 2; i < pts.length; i += 2) {
    const ax = pts[i - 2];
    const az = pts[i - 1];
    const steps = Math.max(1, Math.ceil(Math.hypot(pts[i] - ax, pts[i + 1] - az) / DECK_STEP));
    for (let k = 1; k <= steps; k++) out.push(ax + ((pts[i] - ax) * k) / steps, az + ((pts[i + 1] - az) * k) / steps);
  }
  return out;
}

function projectT(s: { ax: number; az: number; bx: number; bz: number }, x: number, z: number): number {
  const vx = s.bx - s.ax;
  const vz = s.bz - s.az;
  const len2 = vx * vx + vz * vz;
  if (len2 < 1e-12) return 0;
  const t = ((x - s.ax) * vx + (z - s.az) * vz) / len2;
  return t < 0 ? 0 : t > 1 ? 1 : t;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
